import { Lock } from "@mui/icons-material"
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { useStore } from "@nanostores/react"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { getPoolStats } from "../../api/pool-stats-api"

import { getTotalSupply, getTotalSupplyToken } from "../../api/token-api"
import { BuySellProportions } from "../../components/BuySellProportions"
import { PercentChange } from "../../components/PercentChange"
import { SectionInfo } from "../../components/SectionInfo"
import { SimpleNumberFormat } from "../../components/SimpleNumberFormat"
import { SwitchCurrencyButton } from "../../components/SwitchCurrencyButton"
import useTitle from "../../components/useTitle"

import useHopper from "../../hooks/useHopper"

import { getOnchainPoolReserves } from "@/api/pools-api"
import { getTotalBurned } from "@/api/token-burner-api"
import { PriceDisplay } from "@/components/PriceDisplay"
import { usePool } from "@/context/PoolContext"
import ENV from "@/env"
import useLockedShare from "@/hooks/useLockedLiquidity"
import { $poolsOverviewMap } from "@/stores/pools-store"
import { PoolReserves } from "@/types"
import { formatNumberAuto } from "@/utils/format"

export function StatsSection() {
  const { pool, isReversed, isFlipped, baseToken, quoteToken, baseTokenInfo, quoteTokenInfo } =
    usePool()

  const [reserves, setReserves] = useState<PoolReserves | null>(null)
  const [lpBurned, setLpBurned] = useState<string | null>(null)
  const [lpTotalSupply, setLpTotalSupply] = useState<string | null>(null)
  const { data: lockedShare } = useLockedShare(pool.id)

  const overviewMap = useStore($poolsOverviewMap)

  const poolOverview = overviewMap ? overviewMap[pool.id] : undefined

  useEffect(() => {
    if (!pool) return
    getOnchainPoolReserves(pool).then(setReserves)
    getTotalBurned(ENV.VITE_TOKEN_BURNER_PROCESS, pool.id).then(setLpBurned)
    getTotalSupply(pool.id).then(setLpTotalSupply)
  }, [pool])

  const [marketCap, setMarketCap] = useState<number>(0)

  const { data: totalSupplyBT, isLoading: isLoadingTSBT } = useQuery({
    queryKey: ["getTotalSupplyToken", baseToken],
    queryFn: () => getTotalSupplyToken(baseToken),
    staleTime: 1000 * 60 * 10, // 10 minutes
  })

  const { data: totalSupplyQT, isLoading: isLoadingTSQT } = useQuery({
    queryKey: ["getTotalSupplyToken", quoteToken],
    queryFn: () => getTotalSupplyToken(quoteToken),
    staleTime: 1000 * 60 * 10, // 10 minutes
  })

  const { data: stats } = useQuery({
    queryKey: ["getPoolStats", pool.id],
    queryFn: () => getPoolStats(pool.id),
  })

  const { data: quoteTokenUsdPrice } = useHopper(quoteToken, "usd")
  const { data: baseTokenUsdPrice, isLoading: isLoadingBTPrice } = useHopper(baseToken, "usd")

  useTitle(`${baseTokenInfo.ticker} $${formatNumberAuto(baseTokenUsdPrice)}`)

  const liquidity = useMemo(() => {
    if (!reserves || !baseTokenUsdPrice || !quoteTokenUsdPrice) return null

    let reservesBaseToken = reserves.baseToken
    let reservesQuoteToken = reserves.quoteToken

    if (isFlipped) {
      reservesBaseToken = reserves.quoteToken
      reservesQuoteToken = reserves.baseToken

      if (isReversed) {
        reservesBaseToken = reserves.baseToken
        reservesQuoteToken = reserves.quoteToken
      }
    } else {
      if (isReversed) {
        reservesBaseToken = reserves.quoteToken
        reservesQuoteToken = reserves.baseToken
      }
    }

    return (
      (reservesBaseToken / 10 ** baseTokenInfo.denom) * baseTokenUsdPrice +
      (reservesQuoteToken / 10 ** quoteTokenInfo.denom) * quoteTokenUsdPrice
    )
  }, [
    reserves,
    baseTokenUsdPrice,
    quoteTokenUsdPrice,
    baseTokenInfo,
    quoteTokenInfo,
    isFlipped,
    isReversed,
  ])

  const burnedLiq = useMemo(() => {
    if (!liquidity || !lpTotalSupply || !lpBurned) return null

    const value =
      (liquidity * Number.parseInt(lpBurned.toString())) / Number.parseInt(lpTotalSupply.toString())

    return value
  }, [lpBurned, lpTotalSupply, liquidity])

  const lockedLiq = useMemo(() => {
    if (!lockedShare) return null

    return lockedShare?.["One-Year-Locked-Liquidity"]
  }, [lockedShare])

  const lockedAndBurned = useMemo(() => {
    return (burnedLiq || 0) + (lockedLiq || 0)
  }, [burnedLiq, lockedLiq])

  const totalLockedLiqPercentage = useMemo(() => {
    if (!liquidity || !lockedAndBurned) return null

    const value = (100 * lockedAndBurned) / liquidity

    return value
  }, [lockedAndBurned, liquidity])

  useEffect(() => {
    if (!isReversed) {
      if (!totalSupplyBT || !baseTokenUsdPrice) return

      const marketCap = (totalSupplyBT / 10 ** baseTokenInfo.denom) * baseTokenUsdPrice

      setMarketCap(marketCap)
    } else {
      if (!totalSupplyQT || !quoteTokenUsdPrice) return

      const marketCap = (totalSupplyQT / 10 ** quoteTokenInfo.denom) * quoteTokenUsdPrice

      setMarketCap(marketCap)
    }

    return () => {
      setMarketCap(0)
    }
  }, [
    totalSupplyBT,
    baseTokenUsdPrice,
    quoteTokenUsdPrice,
    totalSupplyQT,
    isReversed,
    baseTokenInfo,
    quoteTokenInfo,
  ])

  if (!stats)
    return (
      <>
        <Skeleton height={37.7} />
        <Skeleton height={37.7} />
        <Skeleton height={37.7} />
        <Skeleton height={37.7} />
        <Skeleton height={37.7} />
        <Skeleton height={37.7} />
        <Skeleton height={37.7} />
        <Skeleton height={37.7} />
      </>
    )

  let nPrice1hAgo = 0
  let nPrice6hAgo = 0
  let nPrice24hAgo = 0
  let currentPrice = 0

  if (poolOverview) {
    nPrice1hAgo = poolOverview.token1Price1hAgo
    nPrice6hAgo = poolOverview.token1Price6hAgo
    nPrice24hAgo = poolOverview.token1Price24hAgo
    currentPrice = poolOverview.token1CurrentPrice

    if (isReversed) {
      nPrice1hAgo = poolOverview.token0Price1hAgo
      nPrice6hAgo = poolOverview.token0Price6hAgo
      nPrice24hAgo = poolOverview.token0Price24hAgo
      currentPrice = poolOverview.token0CurrentPrice
    }
  }

  function renderBuyVolume() {
    if (!stats) {
      return <Skeleton width={50} />
    }

    let buyVolume = stats.buyVolume
    let tokenId = baseToken
    let price = quoteTokenUsdPrice

    if (isReversed) {
      buyVolume = stats.sellVolume
      tokenId = quoteToken
      price = baseTokenUsdPrice
    }

    return <SimpleNumberFormat amount={buyVolume} tokenId={tokenId} quoteTokenUsdPrice={price} />
  }

  function renderSellVolume() {
    if (!stats) {
      return <Skeleton width={50} />
    }

    let sellVolume = stats.sellVolume
    let tokenId = baseToken
    let price = quoteTokenUsdPrice

    if (isReversed) {
      sellVolume = stats.buyVolume
      tokenId = quoteToken
      price = baseTokenUsdPrice
    }

    return <SimpleNumberFormat amount={sellVolume} tokenId={tokenId} quoteTokenUsdPrice={price} />
  }

  console.log("STATS", stats)

  return (
    <>
      <SectionInfo
        title="Price"
        value={
          isLoadingBTPrice ? (
            <Skeleton width={50} />
          ) : (
            <Stack direction="row" gap={1}>
              $ {formatNumberAuto(baseTokenUsdPrice)}
              <SwitchCurrencyButton />
            </Stack>
          )
        }
      />
      <SectionInfo
        title="Total Supply"
        value={
          isLoadingTSBT || isLoadingTSQT || !totalSupplyBT ? (
            <Skeleton width={50} />
          ) : (
            <SimpleNumberFormat
              amount={totalSupplyBT}
              tokenId={baseToken}
              needsParsing
              currency={false}
            />
          )
        }
      />
      <SectionInfo
        title="Market Cap"
        value={
          isLoadingTSBT || isLoadingTSQT || !totalSupplyBT ? (
            <Skeleton width={50} />
          ) : (
            <PriceDisplay amount={marketCap} />
          )
        }
      />
      <SectionInfo
        title="Liquidity"
        value={
          liquidity === null ? (
            <Skeleton width={50} />
          ) : (
            <>
              <Stack direction="row" gap={1} alignItems="center">
                <PriceDisplay amount={liquidity} />{" "}
                <Tooltip
                  title={
                    <Stack gap={0} alignItems={"stretch"}>
                      <Stack direction="row" gap={2} justifyItems={"space-between"}>
                        <Typography variant="body2" fontWeight={500} flexGrow={1}>
                          Burned Liquidity
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          <PriceDisplay amount={burnedLiq || 0} />
                        </Typography>
                      </Stack>

                      <Stack direction="row" gap={2} justifyItems={"space-between"}>
                        <Typography variant="body2" fontWeight={500} flexGrow={1}>
                          Locked Liquidity
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          <PriceDisplay amount={lockedLiq || 0} />
                        </Typography>
                      </Stack>

                      <Stack direction="row" gap={2} justifyItems={"space-between"}>
                        <Typography variant="body2" fontWeight={500} flexGrow={1}>
                          Total
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {(totalLockedLiqPercentage || 0)?.toFixed(2)}%
                        </Typography>
                      </Stack>
                    </Stack>
                  }
                >
                  <Box
                    sx={{
                      position: "relative",
                      height: "fit-content",
                      transform: "translate(1px, 1px)",
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      color="success"
                      sx={{
                        opacity: 0.2,
                      }}
                      size={30}
                      thickness={5}
                      value={100}
                    />
                    <CircularProgress
                      variant="determinate"
                      color="success"
                      value={totalLockedLiqPercentage || 0}
                      sx={{
                        position: "absolute",
                        left: 0,
                      }}
                      size={30}
                      thickness={5}
                    />
                    <Lock
                      sx={{
                        fontSize: 14,
                        position: "absolute",
                        left: 8,
                        top: 7,
                      }}
                    />
                  </Box>
                </Tooltip>
              </Stack>
            </>
          )
        }
      />
      {/*       <SectionInfo title="is flipped" value={isFlipped ? "true" : "false"} />
      <SectionInfo title="is reversed" value={isReversed ? "true" : "false"} /> */}
      <SectionInfo
        title="Total Volume"
        value={
          <SimpleNumberFormat
            amount={stats.totalVolume}
            tokenId={quoteToken}
            quoteTokenUsdPrice={isReversed ? baseTokenUsdPrice : quoteTokenUsdPrice}
            mantissa={12}
            totalLength={5}
          />
        }
      />
      <ButtonGroup
        variant="outlined"
        fullWidth
        sx={{
          "& > button": {
            flexDirection: "column",
          },
        }}
      >
        <Button>
          <span>1h</span>
          <PercentChange latestPrice={currentPrice} previousPrice={nPrice1hAgo} />
        </Button>
        <Button>
          <span>6h</span>
          <PercentChange latestPrice={currentPrice} previousPrice={nPrice6hAgo} />
        </Button>
        <Button>
          <span>24h</span>
          <PercentChange latestPrice={currentPrice} previousPrice={nPrice24hAgo} />
        </Button>
      </ButtonGroup>
      <Stack gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <SectionInfo title="Buy vol" direction="column" value={renderBuyVolume()} />
          <SectionInfo
            title="Sell vol"
            direction="column"
            alignItems="flex-end"
            value={renderSellVolume()}
          />
        </Stack>
        <BuySellProportions
          buy={!isReversed ? stats.buyVolume : stats.sellVolume}
          sell={!isReversed ? stats.sellVolume : stats.buyVolume}
        />
      </Stack>
      <Stack gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <SectionInfo
            title="Buy count"
            direction="column"
            value={!isReversed ? stats.buyCount : stats.sellCount}
          />
          <SectionInfo
            title="Sell count"
            direction="column"
            alignItems="flex-end"
            value={!isReversed ? stats.sellCount : stats.buyCount}
          />
        </Stack>
        <BuySellProportions
          buy={!isReversed ? stats.buyCount : stats.sellCount}
          sell={!isReversed ? stats.sellCount : stats.buyCount}
        />
      </Stack>
      <Stack gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <SectionInfo
            title="Buyers"
            direction="column"
            value={!isReversed ? stats.buyers : stats.sellers}
          />
          <SectionInfo
            title="Sellers"
            direction="column"
            alignItems="flex-end"
            value={!isReversed ? stats.sellers : stats.buyers}
          />
        </Stack>
        <BuySellProportions
          buy={!isReversed ? stats.buyers : stats.sellers}
          sell={!isReversed ? stats.sellers : stats.buyers}
        />
      </Stack>
    </>
  )
}
